import axios from 'axios';

const API_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : `http://${window.location.hostname}:4000/api`;

// The session lives in an httpOnly cookie the browser sends automatically
// (withCredentials covers the dev setup, where the API is on another port).
// Page JavaScript never sees the token. X-ShopBook-Client marks requests as
// coming from the app — the server requires it on every change (CSRF guard).
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json', 'X-ShopBook-Client': '1' },
});

// Broadcast account-level errors so any component can react.
api.interceptors.response.use(
  res => res,
  err => {
    const status = err.response?.status;
    const code   = err.response?.data?.code;
    const url    = err.config?.url || '';
    if (status === 403 && code === 'ACCOUNT_FROZEN') {
      window.dispatchEvent(new CustomEvent('account-frozen'));
    }
    if (status === 402 && code === 'SUBSCRIPTION_READ_ONLY') {
      window.dispatchEvent(new CustomEvent('sb:read-only'));
    }
    if (status === 403 && code === 'PASSWORD_CHANGE_REQUIRED') {
      window.dispatchEvent(new CustomEvent('sb:password-change-required'));
    }
    // An expired or revoked session (not a failed sign-in attempt).
    if (status === 401 && !url.startsWith('/auth/login') && !url.startsWith('/auth/register')) {
      window.dispatchEvent(new CustomEvent('sb:session-expired'));
    }
    return Promise.reject(err);
  }
);

// Auth
export const authAPI = {
  register:        (data) => api.post('/auth/register', data),
  login:           (data) => api.post('/auth/login', data),
  logout:          ()     => api.post('/auth/logout'),
  me:              ()     => api.get('/auth/me'),
  sendOTP:         ()     => api.post('/auth/send-email-otp'),
  verifyOTP:       (data) => api.post('/auth/verify-email-otp', data),
  forgotPassword:  (data) => api.post('/auth/forgot-password', data),
  resetPassword:   (data) => api.post('/auth/reset-password', data),
  acceptTerms:     (data) => api.post('/auth/accept-terms', data),
};

// Products
export const productAPI = {
  list:        ()         => api.get('/products'),
  lowStock:    ()         => api.get('/products/low-stock'),
  getById:     (id)       => api.get(`/products/${id}`),
  getSales:    (id)       => api.get(`/products/${id}/sales`),
  create:      (data)     => api.post('/products', data),
  update:      (id, data) => api.put(`/products/${id}`, data),
  void:        (id)       => api.delete(`/products/${id}`),
};

// Purchases (restock)
export const purchaseAPI = {
  list:   ()     => api.get('/purchases'),
  create: (data) => api.post('/purchases', data),
  void:   (id, data) => api.delete(`/purchases/${id}`, { data }),
};

// Stock — own produce (self-produced stock, no purchase cost)
export const stockAPI = {
  ownProduce: (data) => api.post('/stock/own-produce', data),
};

// Sales
export const saleAPI = {
  list:       (params) => api.get('/sales', { params }),
  debtors:    ()       => api.get('/sales/debtors'),
  getById:    (id)     => api.get(`/sales/${id}`),
  create:     (data)   => api.post('/sales', data),
  addPayment: (id, data) => api.post(`/sales/${id}/payments`, data),
  void:       (id, data) => api.post(`/sales/${id}/void`, data),
  voidPayment: (paymentId, data) => api.post(`/sales/payments/${paymentId}/void`, data),
};

// Customers
export const customerAPI = {
  list:   (params) => api.get('/customers', { params }),
  getById: (id)   => api.get(`/customers/${id}`),
  create: (data)  => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  addPayment: (id, data) => api.post(`/customers/${id}/payments`, data),
};

// Financials
export const financialAPI = {
  summary:    (params) => api.get('/financials/summary', { params }),
  detail:     (type, params) => api.get('/financials/detail', { params: { type, ...params } }),
  statements: () => api.get('/financials/statements'),
  charts:     (params) => api.get('/financials/charts', { params }),
};

// Team
export const teamAPI = {
  list:               ()           => api.get('/team'),
  invite:             (data)       => api.post('/team/invite', data),
  updatePermissions:  (id, data)   => api.put(`/team/${id}/permissions`, data),
  deactivate:         (id)         => api.post(`/team/${id}/deactivate`),
  reactivate:         (id)         => api.post(`/team/${id}/reactivate`),
  resetPassword:      (id)         => api.post(`/team/${id}/reset-password`),
};

// Sync
export const syncAPI = {
  push:      (data)   => api.post('/sync', data),
  pull:      (since)  => api.get('/sync/changes', { params: { since } }),
  bootstrap: ()       => api.get('/sync/bootstrap'),
};

// Company (parent company level — owner only)
export const companyAPI = {
  financials:       (params)     => api.get('/company/financials', { params }),
  statements:       ()           => api.get('/company/statements'),
  setInitialCapital:(data)       => api.patch('/company/initial-capital', data),
  fundBalance:      ()           => api.get('/company/fund-balance'),
  listCapital:      ()           => api.get('/company/capital'),
  addCapital:       (data)       => api.post('/company/capital', data),
  voidCapital:      (id)         => api.delete(`/company/capital/${id}`),
  listBiz:          ()           => api.get('/company/businesses'),
  addBiz:           (data)       => api.post('/company/businesses', data),
  ratios:           ()           => api.get('/company/ratios'),
  listAllProducts:  ()                => api.get('/company/products'),
  listAllAssets:    ()                => api.get('/company/assets'),
  createProduct:    (data)            => api.post('/company/products', data),
  updateProduct:    (pid, data)       => api.patch(`/company/products/${pid}`, data),
  getBizSummary:       (id, params)     => api.get(`/company/business/${id}/summary`, { params }),
  getBizStatements:    (id)            => api.get(`/company/business/${id}/statements`),
  getBizCharts:        (id, params)     => api.get(`/company/business/${id}/charts`, { params }),
  getBusinessDetail:   (id)            => api.get(`/company/business/${id}`),
  getBusinessKpiDetail:(id, type)      => api.get(`/company/business/${id}/kpi/${type}`),
  getBizProductDetail: (id, productId) => api.get(`/company/business/${id}/product/${productId}`),
  addBizSale:          (id, data)          => api.post(`/company/business/${id}/sales`, data),
  addBizPurchase:      (id, data)          => api.post(`/company/business/${id}/purchases`, data),
  updateBizPurchase:   (id, pid, data)     => api.patch(`/company/business/${id}/purchases/${pid}`, data),
  voidBizPurchase:     (id, pid)           => api.delete(`/company/business/${id}/purchases/${pid}`),
  addBizExpense:       (id, data)          => api.post(`/company/business/${id}/expenses`, data),
  updateBizExpense:    (id, eid, data)     => api.patch(`/company/business/${id}/expenses/${eid}`, data),
  voidBizExpense:      (id, eid)           => api.delete(`/company/business/${id}/expenses/${eid}`),
  addBizAsset:         (id, data)          => api.post(`/company/business/${id}/assets`, data),
  updateBizAsset:      (id, aid, data)     => api.patch(`/company/business/${id}/assets/${aid}`, data),
  voidBizAsset:        (id, aid)           => api.delete(`/company/business/${id}/assets/${aid}`),
};

// Production runs (bottling / assembly)
export const productionAPI = {
  list:   ()     => api.get('/production'),
  create: (data) => api.post('/production', data),
  void:   (id)   => api.delete(`/production/${id}`),
};

// Expenses (per business, offline-capable)
export const expenseAPI = {
  list:   (params)     => api.get('/expenses', { params }),
  create: (data)       => api.post('/expenses', data),
  update: (id, data)   => api.put(`/expenses/${id}`, data),
  void:   (id)         => api.delete(`/expenses/${id}`),
};

// Settings
export const settingsAPI = {
  previewUpgrade:   (plan, billing_cycle) => api.get('/settings/upgrade-preview', { params: { plan, billing_cycle } }),
  requestUpgrade:   (data) => api.post('/settings/upgrade-request', data),
  getUpgradeStatus: ()     => api.get('/settings/upgrade-request'),
  billing:          ()     => api.get('/settings/billing'),
  changePassword:   (data) => api.patch('/settings/password', data),
  updateEmail:      (data) => api.patch('/settings/email', data),
};

// CSV exports (owner/manager). Downloaded through axios so the auth header is
// sent, then handed to the browser as a file.
export const exportAPI = {
  download: async (kind, params) => {
    const res = await api.get(`/export/${kind}.csv`, { params, responseType: 'blob' });
    const disposition = res.headers['content-disposition'] || '';
    const name = (disposition.match(/filename="([^"]+)"/) || [])[1] || `${kind}.csv`;
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
};

// Activity heartbeat (engagement analytics)
export const activityAPI = {
  ping: () => api.post('/activity/ping'),
};

export default api;
